import { LangText } from '@/components'
import type { MiniMyCouponResponse, UserCouponStatusEnumCode } from '@/service/api/couponPage'
import { UserCouponStatusEnum } from '@/service/api/couponPage'
import { Button, Modal } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { buildCouponShowQrUrl, preFixImgUrl } from '@/utils'
import CouponVisualBlock from './CouponVisualBlock'

/** 我的优惠券：已使用 / 已过期 角标图 */
const STAMP_USED_IMG =
  '45d021545a6f1e05da610365c0bfea35.png'
const STAMP_EXPIRED_IMG =
  '9a381bc4d378d386b4b13d7ffbe76a41.png'

const formatEndDateOnly = (endTime?: string) => {
  if (!endTime) return ''
  const d = dayjs(endTime)
  if (d.isValid()) return d.format('YYYY.MM.DD')
  const head = endTime.split(/[\sT]/)[0]
  return head || ''
}

export interface MineCouponCardProps {
  /** 我的优惠券接口单条（couponDetail + receiveDetail） */
  row: MiniMyCouponResponse
  /** 当前列表 Tab 为「全部」时由 receiveDetail.status 决定；单状态 Tab 为筛选值 */
  status: UserCouponStatusEnumCode
}

const MineCouponCard = ({ row, status }: MineCouponCardProps) => {
  const { t } = useTranslation()
  const [detailOpen, setDetailOpen] = useState(false)
  const [showQrModal, setShowQrModal] = useState(false)
  const qrLink = buildCouponShowQrUrl()
  const coupon = row.couponDetail

  const minConsumeAmount = Number(coupon.minConsume || 0)
  const minSpendLabel =
    minConsumeAmount > 0
      ? t('1b095084', { amount: minConsumeAmount.toLocaleString() })
      : t('04b8f9bf')
  const thresholdLabel =
    minConsumeAmount > 0
      ? t('5e95ddb2', { amount: minConsumeAmount.toLocaleString() })
      : t('04b8f9bf')
  const endDateStr = formatEndDateOnly(coupon.endTime)
  const shortExpiry = endDateStr ? `${endDateStr} ${t('80b1bb8d')}` : '-'
  const fullValidity =
    coupon.startTime && coupon.endTime
      ? `${coupon.startTime} - ${coupon.endTime}`
      : '-'
  const applicableStores =
    coupon.scopeType === 1 ? t('317f12ee') : t('71df1f25')
  const description = coupon.description || '-'
  const merchantName =
    row.merchantName || '-'

  const isUnused = status === UserCouponStatusEnum.UNUSED
  const isUsed = status === UserCouponStatusEnum.USED

  const stampLngKey = isUsed ? '54188f2e' : 'db690fa9'

  const usageHintSlot = (
    <button
      type='button'
      className='inline-flex max-w-full items-center gap-1 text-left text-[12px] text-[#999] transition-opacity hover:opacity-80'
      onClick={() => setDetailOpen(true)}
    >
      <LangText lngKey='1b2bdb8a'>{t('1b2bdb8a')}</LangText>
      <DownOutlined className='text-[10px]' />
    </button>
  )

  const rightExtra = isUnused ? (
    <Button
      type='primary'
      size='small'
      className='!h-9 !min-w-[72px] !rounded-full !border-0 !px-4 !text-[14px] !font-semibold !bg-linear-to-r !from-[#9C59FF] !to-[#5751FF] hover:!opacity-95'
      onClick={e => {
        e.stopPropagation()
        setShowQrModal(true)
      }}
    >
      <LangText lngKey='db7cac02'>{t('db7cac02')}</LangText>
    </Button>
  ) : (
    <img
      src={preFixImgUrl(isUsed ? STAMP_USED_IMG : STAMP_EXPIRED_IMG)}
      alt={t(stampLngKey)}
      className='h-[60px] w-[60px] shrink-0 object-contain select-none'
      draggable={false}
    />
  )

  return (
    <>
      <div className='overflow-hidden rounded-[12px] border border-[#E8E6FF] bg-white shadow-[0_2px_12px_0_rgba(30,34,46,0.06)]'>
        <div className='px-4 pt-3 pb-1'>
          <div className='text-[14px] leading-snug text-[#666666]'>
            {merchantName}
          </div>
        </div>
        <div className='px-4 pb-4'>
          <CouponVisualBlock
            discountType={coupon.discountType}
            discountValue={coupon.discountValue}
            minSpendLabel={minSpendLabel}
            couponTitle={coupon.couponName || '-'}
            shortExpiry={shortExpiry}
            usageHintSlot={usageHintSlot}
            rightExtra={rightExtra}
          />
        </div>
      </div>

      <Modal
        title={<LangText lngKey='1b2bdb8a'>{t('1b2bdb8a')}</LangText>}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={580}
        destroyOnClose
        centered
      >
        <div className='flex flex-col gap-4 pt-2 text-[13px] text-[#666666]'>
          <div className='flex gap-3 justify-between'>
            <LangText lngKey='6a464900' className='shrink-0 text-[#666]' />
            <span className='text-right text-[#333333]'>{thresholdLabel}</span>
          </div>
          <div className='flex gap-3 justify-between'>
            <LangText lngKey='12535291' className='shrink-0 text-[#666]' />
            <span className='text-right text-[#333333]'>{fullValidity}</span>
          </div>
          <div className='flex gap-3 justify-between'>
            <LangText lngKey='4591f4ff' className='shrink-0 text-[#666]' />
            <span className='text-right text-[#333333]'>
              {(coupon.limitPerUser && Number(coupon.limitPerUser) > 0) ? (
                <>
                  {coupon.limitPerUser}
                  <LangText lngKey='92150017' className='ml-0.5' />
                </>
              ) : (
                <LangText lngKey='ae9216ec' />
              )}
            </span>
          </div>
          <div className='flex gap-3 justify-between'>
            <LangText lngKey='5a3fb5a0' className='shrink-0 text-[#666]' />
            <span className='text-right text-[#333333] line-clamp-3'>{applicableStores}</span>
          </div>
          <div className='flex gap-3 justify-between'>
            <LangText lngKey='634051e5' className='shrink-0 text-[#666]' />
            <span className='line-clamp-4 text-right text-[#333333]'>{description}</span>
          </div>
        </div>
      </Modal>

      <Modal
        title={<LangText lngKey='569db4f9'>{t('569db4f9')}</LangText>}
        open={showQrModal}
        onCancel={() => setShowQrModal(false)}
        footer={null}
        width={400}
        centered
        destroyOnClose
      >
        <div className='flex flex-col gap-4 items-center pt-2'>
          {qrLink ? (
            <div className='flex size-[220px] max-w-full items-center justify-center rounded-[12px] border border-[#EEEEEE] bg-white p-3'>
              <QRCodeSVG
                value={qrLink}
                size={196}
                level='M'
                marginSize={2}
                title={t('569db4f9')}
                className='w-full h-auto max-h-full'
              />
            </div>
          ) : null}
          <p className='text-center text-[14px] leading-relaxed text-[#666666]'>
            <LangText lngKey='ba54b773'>{t('ba54b773')}</LangText>
          </p>
        </div>
      </Modal>
    </>
  )
}

export default MineCouponCard
